import { ValidatorInterface } from './ValidatorInterface';
import { AbstractValidator, ValidatorObject } from './AbstractValidator';
import { sprintf } from 'sprintf-js';
import Length from '../../../../source/js/shared/models/Geometry/Length';
import { IsoscelesTriangle } from '../Shapes/IsoscelesTriangle';

interface IsoscelesTriangleConstraintValidatorOptions extends ValidatorObject {
	maximums: Record<string, Length>;
}

/**
 * Validator for isosceles triangle-specific dimensional constraints:
 * - If height = maximum then base ≤ half of maximum
 * - If base = maximum then height ≤ half of maximum
 */
export class IsoscelesTriangleConstraintValidator extends AbstractValidator implements ValidatorInterface {
	private readonly triangleMaximums: Record<string, Length>;

	constructor(options: IsoscelesTriangleConstraintValidatorOptions) {
		super(options);
		this.triangleMaximums = options.maximums;
	}

	getMessage(): string {
		if (super.getCustomMessage()) {
			return super.getCustomMessage();
		}

		const labels = this.getValidatorTranslationStrings().dimensions.labels;
		const maxDimension = this.getMaximumDimension();
		const halfMaximum = Math.floor(maxDimension / 2);
		const triangle = this.instance as IsoscelesTriangle;

		let maxDimensionLabel: string;
		let constraintDimensionsLabel: string;

		if (Math.abs(triangle.getHeight().in_cm() - maxDimension) < 0.1) {
			maxDimensionLabel = labels.height;
			constraintDimensionsLabel = labels.width;
		} else if (Math.abs(triangle.getBase().in_cm() - maxDimension) < 0.1) {
			maxDimensionLabel = labels.width;
			constraintDimensionsLabel = labels.height;
		}

		return sprintf(
			window.measurementSelectorVariables.i18n.validators.dimensions.constraintMax,
			maxDimensionLabel.toLowerCase(),
			maxDimension,
			constraintDimensionsLabel.toLowerCase(),
			halfMaximum,
			'cm'
		);
	}

	isValid(): boolean {

		// Constraints don't apply to cutouts
		if (this.getIsCutout()) {
			return true;
		}

		const maxDimension = this.getMaximumDimension();
		const halfMaximum = maxDimension / 2;
		const triangle = this.instance as IsoscelesTriangle;

		const heightValue = triangle.getHeight().in_cm();
		const baseValue = triangle.getBase().in_cm();

		// If height is at maximum, base must be ≤ half of maximum
		if (Math.abs(heightValue - maxDimension) < 0.1) {
			return baseValue <= halfMaximum;
		}

		// If base is at maximum, height must be ≤ half of maximum
		if (Math.abs(baseValue - maxDimension) < 0.1) {
			return heightValue <= halfMaximum;
		}

		return true;
	}

	private getMaximumDimension(): number {
		const heightMax = this.triangleMaximums.height.in_cm();
		const baseMax = this.triangleMaximums.base.in_cm();

		// They should all be the same, but take the maximum to be safe
		return Math.max(heightMax, baseMax);
	}
}
